import '../../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x } from '../../../node_modules/lit-html/lit-html.js';
import '../../../node_modules/lit-element/lit-element.js';
import { dedupeMixin } from '../../../node_modules/@open-wc/dedupe-mixin/src/dedupeMixin.js';

/**
 * @template {Constructor<any>} TBase
 * @param {TBase} superclass
 * @return {TBase & Constructor<ScopedElementsHost>}
 */
function ScopedElementsMixinImplementation(superclass) {
    var _a;
    return _a = class ScopedElementsHost extends superclass {
            /**
             * Obtains the scoped elements definitions map if specified.
             * If scoped elements are required, this will be overridden in the component.
             *
             * @returns {ScopedElementsMap}
             */
            static get scopedElements() {
                return {};
            }
            firstUpdated() {
                const { scopedElements } = this.constructor;
                for (const [tagName, klass] of Object.entries(scopedElements)) {
                    if (klass) {
                        this.defineScopedElement(tagName, klass);
                    }
                    else {
                        console.warn(`Component definition for "${tagName}" has not been loaded. Please load it to scope its functionality.`);
                    }
                }
                super.firstUpdated();
                // let's trigger a repaint to ensure the scoped tags are used
                this.requestUpdate();
            }
            /**
             * Defines a scoped element.
             *
             * @param {string} tagName
             * @param {CustomElementConstructor} klass
             */
            defineScopedElement(tagName, klass) {
                const { namespace, scopedTagList } = this.constructor;
                const scopedTag = scopedTagList.get(`${namespace}_${tagName}`);
                if (scopedTag) {
                    return scopedTag;
                }
                // create a scoped version of the tag name with a random number at the end
                const randomSuffix = () => Math.round(Math.random() * 88888888 + 11111111).toString();
                let scopedTagName = `${tagName}-${randomSuffix()}`;
                while (customElements.get(scopedTagName)) {
                    scopedTagName = `${tagName}-${randomSuffix()}`;
                }
                // add scoped tag to internal dictionary
                scopedTagList.set(`${namespace}_${tagName}`, scopedTagName);
                // register the component with the new scoped tag
                // the same constructor cannot be used multiple times so we have to extend it just in case
                customElements.define(scopedTagName, class extends klass {
                });
            }
            /**
             * This method is a template tag that is used to preprocess an HTML template for use in
             * Lit's html template tag in order to replace scoped components with their scoped tag names.
             * The scoped HTML will then be passed through Lit's html template tag (used as a regular function)
             * in order to transform it for use in Lit.
             * @param strings the template strings broken down
             * @param _ spread of the properties passed into the template tag that need to be passed to html
             * @returns TemplateResult (generated by the html template tag)
             */
            scopeTemplate(strings, ..._) {
                // create original tag names and regexes once ahead of time for reuse
                // @ts-ignore
                const scopedHelpers = Array.from(this.constructor.scopedTagList).map(([namespacedOgTagName, scopedTagName]) => {
                    // @ts-ignore
                    const ogTagName = namespacedOgTagName.replace(`${this.constructor.namespace}_`, '');
                    return {
                        ogTagName,
                        scopedTagName,
                        ogOpeningTagRegex: new RegExp(`<${ogTagName}`, 'igm'),
                        ogClosingTagRegex: new RegExp(`</${ogTagName}`, 'igm'),
                    };
                });
                // we'll loop through all of the scoped components and update their tags in the template
                const mappedStrings = strings.map(scopedString => {
                    Array.from(scopedHelpers).forEach(({ ogTagName, scopedTagName, ogOpeningTagRegex, ogClosingTagRegex }) => {
                        scopedString = scopedString
                            .replace(ogOpeningTagRegex, `<${scopedTagName} data-tag-name="${ogTagName}"`)
                            .replace(ogClosingTagRegex, `</${scopedTagName}`);
                    });
                    return scopedString;
                });
                // we need to add the immutable raw property so that the array properly conforms to the expected type
                // for Lit's html template tag
                Object.defineProperty(mappedStrings, 'raw', {
                    value: [...mappedStrings],
                    writable: false,
                });
                return x(mappedStrings, ..._);
            }
        },
        /**
         * Stores the mapping of namespaced scoped elements. We store them with the namespace
         * because there may be a case where a component is defined with multiple namespaces.
         */
        _a.scopedTagList = new Map(),
        _a;
}
const ScopedElementsMixin = dedupeMixin(ScopedElementsMixinImplementation);

export { ScopedElementsMixin };
//# sourceMappingURL=scoped-elements.mixin.js.map
